<template>
    <GDialog
        :model-value="show"
        max-width="500"
        border-radius="10"
        @update:model-value="hideModal"
    >
        <div class="transaction-documents-modal">
            <div class="modal-title text-h1 text-bold">Файли</div>

            <div class="modal-body">
                <template v-if="documents.length">
                    <div class="documents-grid">
                        <div
                            v-for="(document, index) in documents"
                            :key="document.uuid"
                            class="document-item shadow-sm"
                        >
                            <img
                                v-if="document.previewUrl"
                                class="item-image"
                                :src="document.previewUrl"
                                :alt="document.name"
                                @click="openLightbox(index)"
                            />
                            <div v-else class="item-placeholder text-h6 text-grey">
                                {{ document.name }}
                            </div>
                            <button
                                type="button"
                                class="download-btn"
                                :disabled="!document.blob"
                                @click.stop="downloadDocument(document)"
                            >
                                <img src="@/assets/images/download-icon.svg" alt="" />
                            </button>
                        </div>
                    </div>

                    <vue-easy-lightbox
                        :visible="lightboxVisible"
                        :imgs="imageUrls"
                        :index="currentIndex"
                        :scroll-disabled="true"
                        :rotate-disabled="true"
                        :move-disabled="true"
                        :zoom-disabled="true"
                        @hide="lightboxVisible = false"
                    />
                </template>

                <div v-else-if="!isLoading" class="empty-state text-h5 text-grey text-center">
                    Немає документів
                </div>
            </div>

            <LoaderComponent v-if="isLoading" />
        </div>
    </GDialog>
</template>

<script>
import fetchWrapper, { api } from '@/helpers/fetch-wrapper.js'
import LoaderComponent from '@/components/LoaderComponent.vue'
import VueEasyLightbox from 'vue-easy-lightbox'

export default {
    name: 'TransactionDocumentsModal',
    components: {
        LoaderComponent,
        VueEasyLightbox
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        transactionId: {
            type: [Number, null],
            default: null
        }
    },
    emits: ['close'],
    data() {
        return {
            documents: [],
            isLoading: false,
            lightboxVisible: false,
            currentIndex: 0
        }
    },
    computed: {
        imageUrls() {
            return this.documents
                .filter((document) => document.previewUrl)
                .map((document) => document.previewUrl)
        }
    },
    watch: {
        show: {
            immediate: true,
            handler(value) {
                if (value && this.transactionId) {
                    this.loadDocuments()
                }

                if (!value) {
                    this.resetDocuments()
                }
            }
        }
    },
    beforeUnmount() {
        this.resetDocuments()
    },
    methods: {
        getDownloadUrl(uuid) {
            return `${import.meta.env.VITE_FINANCE_URL}/transactions/download-file/${uuid}`
        },

        async loadDocuments() {
            this.isLoading = true
            this.resetDocuments()

            try {
                const response = await fetchWrapper.post(
                    `${import.meta.env.VITE_FINANCE_URL}/transactions/load-documents/${this.transactionId}`
                )
                const documents = response.data || []

                this.documents = await Promise.all(
                    documents.map(async (document) => {
                        try {
                            const fileResponse = await api.get(this.getDownloadUrl(document.uuid), {
                                responseType: 'blob'
                            })
                            const blob = fileResponse.data
                            const isImage = (document.mime_type || blob.type || '').startsWith('image/')

                            return {
                                uuid: document.uuid,
                                name: document.name,
                                mime_type: document.mime_type,
                                blob,
                                previewUrl: isImage ? URL.createObjectURL(blob) : null
                            }
                        } catch (error) {
                            return {
                                uuid: document.uuid,
                                name: document.name,
                                mime_type: document.mime_type,
                                blob: null,
                                previewUrl: null
                            }
                        }
                    })
                )
            } finally {
                this.isLoading = false
            }
        },

        openLightbox(index) {
            const previewUrl = this.documents[index]?.previewUrl

            if (!previewUrl) {
                return
            }

            this.currentIndex = this.imageUrls.indexOf(previewUrl)
            this.lightboxVisible = true
        },

        downloadDocument(document) {
            if (!document.blob) {
                return
            }

            const url = URL.createObjectURL(document.blob)
            const link = window.document.createElement('a')

            link.href = url
            link.download = document.name || `document-${document.uuid}`
            link.click()

            URL.revokeObjectURL(url)
        },

        resetDocuments() {
            this.documents.forEach((document) => {
                if (document.previewUrl) {
                    URL.revokeObjectURL(document.previewUrl)
                }
            })
            this.documents = []
            this.lightboxVisible = false
            this.currentIndex = 0
        },

        hideModal() {
            this.resetDocuments()
            this.$emit('close')
        }
    }
}
</script>

<style scoped lang="scss">
.transaction-documents-modal {
    position: relative;
    padding: 2rem 1.25rem 2.5rem;

    .modal-title {
        margin-bottom: 1.25rem;
        text-align: center;
    }

    .modal-body {
        max-height: calc(90vh - 8rem);
        overflow-y: auto;
    }

    .documents-grid {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .document-item {
        position: relative;
        width: calc(50% - 0.25rem);
        margin-bottom: 0.5rem;
        border-radius: 0.5rem;
        overflow: hidden;
        padding-top: 28%;
        background: $grey-light;
    }

    .item-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        cursor: pointer;
    }

    .item-placeholder {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.75rem;
        text-align: center;
        word-break: break-word;
    }

    .download-btn {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 5;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        border: none;
        border-radius: 0.375rem;
        background: $white;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
        cursor: pointer;

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }
    }

    .empty-state {
        padding: 1rem 0;
    }

    .loader-overlay {
        background: rgba($white, 0.8);
    }

    :deep(.vel-toolbar) {
        .toolbar-btn__resize {
            display: none;
        }
    }
}
</style>
